/**
 * Pure module access policy for the compliance frontend.
 *
 * Single source of truth for:
 * - whether a user may access a module (`canAccessModule`, `getModuleAccessDecision`)
 * - post-login / denied redirects (`getDefaultAuthenticatedPath`, `getAccessDeniedRedirectPath`)
 * - URL → module resolution (`resolveModuleKeyForPath`)
 *
 * Does not import React or page components. Sidebar and route guards call into this module.
 *
 * @see docs/module-access.md
 * @module modules/appModuleAccess
 */

import { APP_MODULE_CATALOG, getAppModuleConfig } from "./appModuleCatalog";
import { APP_MODULE_KEYS, USER_ROLES, normalizeAppModuleKey } from "./appModuleKeys";

/** Private route shown when `role === evaluator` but `Evaluations` ∉ `userData.modules`. */
export const ACCOUNT_MISCONFIGURED_PATH = "/account/misconfigured";

/**
 * Structured reasons returned by {@link getModuleAccessDecision}.
 * Useful for tests and debugging; consumers usually read `.allowed` only.
 */
export const MODULE_ACCESS_REASONS = Object.freeze({
  AUTH_REQUIRED: "AUTH_REQUIRED",
  UNKNOWN_MODULE: "UNKNOWN_MODULE",
  ACCOUNT_MISCONFIGURED: "ACCOUNT_MISCONFIGURED",
  ROLE_DENIED: "ROLE_DENIED",
  EVALUATOR_RESTRICTED: "EVALUATOR_RESTRICTED",
  MISSING_MODULE: "MISSING_MODULE",
  DEV_BYPASS_ALLOWED: "DEV_BYPASS_ALLOWED",
  ALLOWED: "ALLOWED",
});

/**
 * When `true`, registry entries with `devOnly: true` may bypass `userData.modules`
 * in local development. Does not bypass role rules or evaluator restrictions.
 *
 * Requires `NODE_ENV === "development"` and `REACT_APP_ENABLE_DEV_MODULE_BYPASS=true`.
 */
export const isDevModuleBypassEnabled =
  process.env.NODE_ENV === "development" &&
  String(process.env.REACT_APP_ENABLE_DEV_MODULE_BYPASS).toLowerCase() === "true";

/** @param {unknown} role */
function normalizeRole(role) {
  return typeof role === "string" ? role.trim().toLowerCase() : "";
}

/**
 * Case-insensitive set of module keys assigned to the user.
 * Unknown API values are dropped by {@link normalizeAppModuleKey}.
 *
 * @param {Object|undefined} userData
 * @returns {Set<string>} lowercase module keys
 */
function getUserModuleKeys(userData) {
  if (!Array.isArray(userData?.modules)) return new Set();
  return new Set(
    userData.modules
      .map((moduleKey) => normalizeAppModuleKey(moduleKey))
      .filter(Boolean)
      .map((moduleKey) => moduleKey.toLowerCase())
  );
}

/**
 * @param {string} moduleKey — registry / {@link APP_MODULE_KEYS} value
 * @param {Object|undefined} userData
 */
function userHasModule(moduleKey, userData) {
  const normalizedKey = normalizeAppModuleKey(moduleKey);
  if (!normalizedKey) return false;
  return getUserModuleKeys(userData).has(normalizedKey.toLowerCase());
}

/**
 * @typedef {Object} ModuleAccessDecision
 * @property {boolean} allowed
 * @property {string} reason — {@link MODULE_ACCESS_REASONS}
 * @property {string} moduleKey
 * @property {string} [redirectTo] — present when `allowed === false`
 */

/** @param {ModuleAccessDecision} decision */
function accessDecision({ allowed, reason, moduleKey, redirectTo }) {
  return {
    allowed,
    reason,
    moduleKey,
    ...(redirectTo ? { redirectTo } : {}),
  };
}

/**
 * Evaluator accounts must have `Evaluations` in `userData.modules`.
 * All module access is blocked until an admin fixes assignment.
 *
 * @param {Object|undefined} userData
 * @returns {boolean}
 */
export function isEvaluatorAccountMisconfigured(userData) {
  return normalizeRole(userData?.role) === USER_ROLES.EVALUATOR &&
    !userHasModule(APP_MODULE_KEYS.EVALUATIONS, userData);
}

/**
 * Generic misconfiguration hook — currently delegates to evaluator check only.
 *
 * @param {Object|undefined} userData
 * @returns {boolean}
 */
export function isAccountMisconfigured(userData) {
  return isEvaluatorAccountMisconfigured(userData);
}

/**
 * Landing path after login, for authenticated `/`, and header home link.
 *
 * | Account | Path |
 * |---------|------|
 * | Misconfigured evaluator | `/account/misconfigured` |
 * | Valid evaluator | `/evaluations` |
 * | Everyone else | `/dashboard` |
 *
 * @param {Object|undefined} userData
 * @returns {string}
 */
export function getDefaultAuthenticatedPath(userData) {
  if (isEvaluatorAccountMisconfigured(userData)) return ACCOUNT_MISCONFIGURED_PATH;
  if (normalizeRole(userData?.role) === USER_ROLES.EVALUATOR) {
    return APP_MODULE_CATALOG[APP_MODULE_KEYS.EVALUATIONS].defaultPath;
  }
  return APP_MODULE_CATALOG[APP_MODULE_KEYS.DASHBOARD].defaultPath;
}

/**
 * Where to send a user who failed a module access check.
 * Currently mirrors {@link getDefaultAuthenticatedPath}.
 *
 * @param {Object|undefined} userData
 * @returns {string}
 */
export function getAccessDeniedRedirectPath(userData) {
  return getDefaultAuthenticatedPath(userData);
}

/**
 * Full access evaluation for one module.
 *
 * Order of checks: auth → known module → misconfigured evaluator → evaluator-only
 * restriction → denied/required roles → authenticated-only modules → assigned modules
 * → dev bypass → deny.
 *
 * @param {string} moduleKey
 * @param {Object|undefined} userData
 * @returns {ModuleAccessDecision}
 */
export function getModuleAccessDecision(moduleKey, userData) {
  const moduleConfig = getAppModuleConfig(moduleKey);
  const normalizedModuleKey = moduleConfig?.key || normalizeAppModuleKey(moduleKey) || moduleKey;

  // if no user data, access decision should be that auth is still required, redirect to the login page
  if (!userData) {
    return accessDecision({
      allowed: false,
      reason: MODULE_ACCESS_REASONS.AUTH_REQUIRED,
      moduleKey: normalizedModuleKey,
      redirectTo: "/login",
    });
  }
  // if the module key is not found in the registry, return unknown module decision, and redirect to the access denied path
  if (!moduleConfig) {
    return accessDecision({
      allowed: false,
      reason: MODULE_ACCESS_REASONS.UNKNOWN_MODULE,
      moduleKey: normalizedModuleKey,
      redirectTo: getAccessDeniedRedirectPath(userData),
    });
  }

  // if the user is an evaluator and does not have the evaluations module, return account misconfigured decision, and redirect to the account misconfigured page
  if (isEvaluatorAccountMisconfigured(userData)) {
    return accessDecision({
      allowed: false,
      reason: MODULE_ACCESS_REASONS.ACCOUNT_MISCONFIGURED,
      moduleKey: moduleConfig.key,
      redirectTo: ACCOUNT_MISCONFIGURED_PATH,
    });
  }

  // get the role of the user
  const role = normalizeRole(userData?.role);
  // get the required roles for the module
  const requiredRoles = moduleConfig.access.requiredRoles || [];
  // get the denied roles for the module
  const deniedRoles = moduleConfig.access.deniedRoles || [];

  // Evaluators may only access Evaluations (and only if assigned that module).
  if (role === USER_ROLES.EVALUATOR) {
    // if the module key is not the evaluations module, return evaluator restricted decision, and redirect to the access denied path
    if (moduleConfig.key !== APP_MODULE_KEYS.EVALUATIONS) {
      return accessDecision({
        allowed: false,
        reason: MODULE_ACCESS_REASONS.EVALUATOR_RESTRICTED,
        moduleKey: moduleConfig.key,
        redirectTo: getAccessDeniedRedirectPath(userData),
      });
    }

    // if the user has the evaluations module, return allowed decision
    if (userHasModule(moduleConfig.key, userData)) {
      return accessDecision({
        allowed: true,
        reason: MODULE_ACCESS_REASONS.ALLOWED,
        moduleKey: moduleConfig.key,
      });
    }

    // if the user does not have the evaluations module, return missing module decision, and redirect to the account misconfigured page
    return accessDecision({
      allowed: false,
      reason: MODULE_ACCESS_REASONS.MISSING_MODULE,
      moduleKey: moduleConfig.key,
      redirectTo: ACCOUNT_MISCONFIGURED_PATH,
    });
  }

  // if the role is denied for the module, return role denied decision, and redirect to the access denied path
  if (deniedRoles.includes(role)) {
    return accessDecision({
      allowed: false,
      reason: MODULE_ACCESS_REASONS.ROLE_DENIED,
      moduleKey: moduleConfig.key,
      redirectTo: getAccessDeniedRedirectPath(userData),
    });
  }

  // if the required roles for the module are not included in the user's role, return role denied decision, and redirect to the access denied path
  if (requiredRoles.length > 0 && !requiredRoles.includes(role)) {
    return accessDecision({
      allowed: false,
      reason: MODULE_ACCESS_REASONS.ROLE_DENIED,
      moduleKey: moduleConfig.key,
      redirectTo: getAccessDeniedRedirectPath(userData),
    });
  }

  // Dashboard: authenticated only; not listed in userData.modules.
  if (moduleConfig.access.type === "authenticated") {
    return accessDecision({
      allowed: true,
      reason: MODULE_ACCESS_REASONS.ALLOWED,
      moduleKey: moduleConfig.key,
    });
  }
  // if the user has the module in their userData.modules, return allowed decision
  if (userHasModule(moduleConfig.key, userData)) {
    return accessDecision({
      allowed: true,
      reason: MODULE_ACCESS_REASONS.ALLOWED,
      moduleKey: moduleConfig.key,
    });
  }
  // if the module is a dev only module and the dev module bypass is enabled, return dev bypass allowed decision
  if (moduleConfig.devOnly && isDevModuleBypassEnabled) {
    return accessDecision({
      allowed: true,
      reason: MODULE_ACCESS_REASONS.DEV_BYPASS_ALLOWED,
      moduleKey: moduleConfig.key,
    });
  }

  // if the user does not have the module in their userData.modules and the module is not a dev only module, return missing module decision, and redirect to the access denied path
  return accessDecision({
    allowed: false,
    reason: MODULE_ACCESS_REASONS.MISSING_MODULE,
    moduleKey: moduleConfig.key,
    redirectTo: getAccessDeniedRedirectPath(userData),
  });
}

/**
 * @param {string} moduleKey
 * @param {Object|undefined} userData
 * @returns {boolean}
 */
export function canAccessModule(moduleKey, userData) {
  // return the allowed decision from the module access decision
  return getModuleAccessDecision(moduleKey, userData).allowed;
}

/** Strip query/hash and normalize trailing slashes for path comparison. */
function normalizePath(path) {
  const pathname = String(path || "").split(/[?#]/)[0] || "/";
  const withLeadingSlash = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return withLeadingSlash.replace(/\/+$/, "") || "/";
}

/** @param {string} path */
function getPathSegments(path) {
  const normalizedPath = normalizePath(path);
  if (normalizedPath === "/") return [];
  return normalizedPath.split("/").filter(Boolean);
}

/**
 * Match a concrete URL against a registry route pattern (`:param` segments).
 * Used for flat `routePaths` and sidebar active-state matching.
 *
 * @param {string} path — current pathname
 * @param {string} routePattern — e.g. `/actions/:actionId`
 * @returns {boolean}
 */
export function doesPathMatchRoutePattern(path, routePattern) {
  const pathSegments = getPathSegments(path);
  const patternSegments = getPathSegments(routePattern);

  if (pathSegments.length !== patternSegments.length) return false;

  return patternSegments.every((patternSegment, index) => {
    if (patternSegment.startsWith(":")) return Boolean(pathSegments[index]);
    return patternSegment === pathSegments[index];
  });
}

/**
 * Convert shell `mountPath` (`/actions/*`) to prefix for prefix matching.
 *
 * @param {string} mountPath
 * @returns {string}
 */
function getShellMountBasePath(mountPath) {
  return normalizePath(String(mountPath || "").replace(/\/\*$/, ""));
}

/**
 * True when `path` is under a shell module's mount prefix.
 *
 * @param {string} path
 * @param {string} mountPath — e.g. `/entities/*`
 */
function doesPathMatchShellMountPath(path, mountPath) {
  const basePath = getShellMountBasePath(mountPath);
  const normalizedPath = normalizePath(path);

  return normalizedPath === basePath || normalizedPath.startsWith(`${basePath}/`);
}

/**
 * Map a URL to the owning module key.
 *
 * 1. Longest match against explicit `routePaths` (flat + known shell children).
 * 2. Fallback: shell `routing.mountPath` prefix (future child routes not yet in `routePaths`).
 *
 * @param {string} path
 * @returns {string|undefined} module key from registry
 */
export function resolveModuleKeyForPath(path) {
  const routeEntries = Object.values(APP_MODULE_CATALOG)
    .flatMap((moduleConfig) =>
      moduleConfig.routePaths.map((routePath) => ({
        moduleKey: moduleConfig.key,
        routePath,
      }))
    )
    .sort((left, right) => getPathSegments(right.routePath).length - getPathSegments(left.routePath).length);

  const concreteRouteMatch = routeEntries.find(({ routePath }) => doesPathMatchRoutePattern(path, routePath));
  if (concreteRouteMatch) return concreteRouteMatch.moduleKey;

  // Shell modules: match any path under mountPath (e.g. /newModule/create before it is listed in routePaths).
  const shellRouteEntries = Object.values(APP_MODULE_CATALOG)
    .filter((moduleConfig) => moduleConfig.routing?.mode === "shell")
    .map((moduleConfig) => ({
      moduleKey: moduleConfig.key,
      mountPath: moduleConfig.routing.mountPath,
      basePath: getShellMountBasePath(moduleConfig.routing.mountPath),
    }))
    .sort((left, right) => getPathSegments(right.basePath).length - getPathSegments(left.basePath).length);

  return shellRouteEntries.find(({ mountPath }) => doesPathMatchShellMountPath(path, mountPath))?.moduleKey;
}

/**
 * @param {string} path
 * @param {Object|undefined} userData
 * @returns {boolean}
 */
export function canAccessPath(path, userData) {
  const moduleKey = resolveModuleKeyForPath(path);
  return moduleKey ? canAccessModule(moduleKey, userData) : false;
}
